import { useState } from "react";
import { Navigate, useLocation, useNavigate } from "react-router-dom";
import { ArrowLeft } from "@phosphor-icons/react";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { api } from "@/lib/api";
import { Logo } from "@/lib/brand";
import { useAdmin, useConsoleLanguage } from "@/admin/AuthContext";

function PasswordStep({ onDone }) {
  const [email, setEmail] = useState("");
  const [password, setPassword] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      onDone(await api("/admin/auth/login", { auth: false, body: { email: email.trim(), password } }), email.trim());
    } catch (err) {
      setError(err.message);
    } finally {
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="admin-login-form">
      <div>
        <Label htmlFor="admin-email">Email</Label>
        <Input id="admin-email" type="email" autoComplete="username" required value={email} onChange={(e) => setEmail(e.target.value)}
          className="mt-2 h-12 rounded-xl bg-white" data-testid="admin-login-email-input" />
      </div>
      <div>
        <Label htmlFor="admin-password">Password</Label>
        <Input id="admin-password" type="password" autoComplete="current-password" required value={password} onChange={(e) => setPassword(e.target.value)}
          className="mt-2 h-12 rounded-xl bg-white" data-testid="admin-login-password-input" />
      </div>
      {error && <p className="cw-error text-sm" role="alert" data-testid="admin-login-error">{error}</p>}
      <Button type="submit" disabled={busy || !email || !password} className="h-12 w-full rounded-full text-base" data-testid="admin-login-submit-button">
        {busy ? "Checking…" : "Continue"}
      </Button>
    </form>
  );
}

function CodeStep({ challenge, email, onBack, onToken }) {
  const [code, setCode] = useState("");
  const [busy, setBusy] = useState(false);
  const [error, setError] = useState(null);

  async function submit(e) {
    e.preventDefault();
    setBusy(true);
    setError(null);
    try {
      const res = await api("/admin/auth/verify-2fa", { auth: false, body: { challenge_id: challenge, code: code.trim() } });
      await onToken(res.token);
    } catch (err) {
      setError(err.message);
      setBusy(false);
    }
  }

  return (
    <form onSubmit={submit} className="space-y-5" data-testid="admin-2fa-form">
      <p className="cw-soft text-sm leading-6" data-testid="admin-2fa-hint">We emailed a 6-digit code to <strong className="font-medium text-foreground">{email}</strong>. It expires in 10 minutes.</p>
      <div>
        <Label htmlFor="admin-code">Verification code</Label>
        <Input id="admin-code" inputMode="numeric" autoComplete="one-time-code" autoFocus maxLength={8} value={code}
          onChange={(e) => setCode(e.target.value.replace(/\D/g, ""))}
          className="mt-2 h-14 rounded-xl bg-white text-center text-2xl tracking-[0.5em]" data-testid="admin-2fa-code-input" />
      </div>
      {error && <p className="cw-error text-sm" role="alert" data-testid="admin-2fa-error">{error}</p>}
      <Button type="submit" disabled={busy || code.length < 4} className="h-12 w-full rounded-full text-base" data-testid="admin-2fa-submit-button">
        {busy ? "Verifying…" : "Verify and sign in"}
      </Button>
      <button type="button" onClick={onBack} className="cw-soft inline-flex items-center gap-2 rounded text-sm hover:text-foreground" data-testid="admin-2fa-back-button">
        <ArrowLeft size={16} aria-hidden /> Use a different account
      </button>
    </form>
  );
}

export default function AdminLogin() {
  const { admin, ready, signIn } = useAdmin();
  useConsoleLanguage();
  const navigate = useNavigate();
  const loc = useLocation();
  const [step, setStep] = useState({ name: "password" });

  if (ready && admin) return <Navigate to={loc.state?.from || "/admin"} replace />;

  const finish = async (token) => {
    await signIn(token);
    navigate(loc.state?.from || "/admin", { replace: true });
  };

  const onPassword = (res, email) => (res.requires_2fa ? setStep({ name: "code", challenge: res.challenge_id, email }) : finish(res.token));

  return (
    <div className="flex min-h-screen items-center justify-center px-5 py-16" data-testid="admin-login-page">
      <div className="cw-rise w-full max-w-md">
        <Logo className="h-9" testId="admin-login-logo" />
        <div className="cw-shadow mt-10 rounded-[28px] border border-border bg-white p-8 sm:p-10">
          <h1 className="text-2xl font-semibold tracking-tight">{step.name === "code" ? "Check your email" : "Admin sign in"}</h1>
          <p className="cw-muted mb-8 mt-2 text-sm">Crushwink console · authorized team only</p>
          {step.name === "password"
            ? <PasswordStep onDone={onPassword} />
            : <CodeStep challenge={step.challenge} email={step.email} onBack={() => setStep({ name: "password" })} onToken={finish} />}
        </div>
      </div>
    </div>
  );
}
